import { useCallback, useMemo } from "react";
import { useColumnResize as useColumnResizeBase } from "@/hooks/use-column-resize";
import { COLUMN_REGISTRY, DEFAULT_COLUMN_WIDTHS } from "../config/columns";

const STORAGE_KEY = "ordersRecapColumnWidths";

// Columns the user can't drag-resize. Their width is always the
// registry's value — never localStorage's. Stops a stale stored width
// (e.g. an old `status: 70` from before the column was shrunk) from
// pinning the column at the previous size forever after a config change.
const NON_RESIZABLE_COLUMNS: ReadonlySet<string> = new Set(
  COLUMN_REGISTRY.filter((col) => col.resizable === false).map((col) => col.id),
);

interface UseColumnResizeOptions {
  enabled?: boolean;
  /**
   * Override the resize config so a sibling workspace (transport) resizes
   * against its own defaults under its own storage key. All optional and
   * default to recap's sales/purchases values, so omitting them keeps the
   * existing behavior. Pass STABLE references (module constants) — the
   * underlying hook reloads when `defaultWidths`/`storageKey` identity changes.
   */
  storageKey?: string;
  defaultWidths?: Record<string, number>;
  nonResizableColumns?: ReadonlySet<string>;
}

export function useColumnResize({
  enabled = true,
  storageKey = STORAGE_KEY,
  defaultWidths = DEFAULT_COLUMN_WIDTHS,
  nonResizableColumns = NON_RESIZABLE_COLUMNS,
}: UseColumnResizeOptions = {}) {
  const {
    getWidth: getStoredWidth,
    resizingCols,
    handleResizeStart,
  } = useColumnResizeBase({
    storageKey,
    defaultWidths,
    enabled,
  });

  const getWidth = useCallback(
    (colId: string) =>
      nonResizableColumns.has(colId)
        ? (defaultWidths[colId] ?? getStoredWidth(colId))
        : getStoredWidth(colId),
    [getStoredWidth, nonResizableColumns, defaultWidths],
  );

  return useMemo(
    () => ({ getWidth, resizingCols, handleResizeStart }),
    [getWidth, resizingCols, handleResizeStart],
  );
}
